import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { IBaseResponse } from '@carm/_utils/models';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BusinessProgramIdentifier,
  IBn9RegistrationModel,
  IBusinessRegistrationDraftResponse,
  IPrePopulateBusinessRegistrationRequest,
  IValidateBusinessModel,
  IValidateBusinessTransactionsModel,
  IValidateBusinessTransactionsRequest,
  IValidateBusinessTransactionsSubmitModel,
} from '../models';
import { IProgramListResponse, LoaderSpinnerTypes } from '@carm/_core';
import { map } from 'rxjs/operators';
import { getValidateBusinessInfoSubmitModel, mapModelToDraftRequest, mapModelToSubmitRequest } from '../converters/bn9-registration-converters';

@Injectable()
export class RegisterBusinessService extends BaseApiService {
  private get registerBusinessBaseUrl(): string {
    return environment.business.business.registration.baseUrl;
  }

  constructor(protected injector: Injector) {
    super(injector);
  }

  public getDraft(
    draftUid: string
  ): Observable<IBusinessRegistrationDraftResponse> {
    return this.httpAuthGet<IBusinessRegistrationDraftResponse>(
      `${this.registerBusinessBaseUrl}/draft/${draftUid}`
    );
  }

  public getProgramList(businessId: string): Observable<IProgramListResponse> {
    const endPoint = `${environment.baseApiUrls}/business/${businessId}/programs/program-account-list`;
    return this.httpAuthGet<IProgramListResponse>(endPoint);
  }

  public submitBusinessRegistration(
    model: IBn9RegistrationModel
  ): Observable<IBaseResponse> {
    const submitRequest = mapModelToSubmitRequest(model);
    return this.httpAuthPost<IBaseResponse>(
      `${this.registerBusinessBaseUrl}/draft/${model.uid}/registerbusiness`,
      submitRequest
    );
  }

  public saveDraft(
    model: IBn9RegistrationModel | IValidateBusinessModel,
    draftName?: string
  ): Observable<IBaseResponse> {
    const draft = mapModelToDraftRequest(model);
    draft.name =
      draftName ||
      draft.name ||
      this.translateService.instant('bn9-registration.default_draftname');
    return !!draft.uid
      ? this.httpAuthPut<IBaseResponse>(
          `${this.registerBusinessBaseUrl}/draft/${draft.uid}/savemedia`,
          draft
        )
      : this.httpAuthPost<IBaseResponse>(
          `${this.registerBusinessBaseUrl}/draft/create`,
          draft
        );
  }

  public deleteDraft(
    draftId: string
  ): Observable<IBaseResponse> {
    return this.httpAuthDelete<IBaseResponse>(
      `${this.registerBusinessBaseUrl}/draft/${draftId}/delete`,
    );
  }

  public createDraftForValidatedEntity(
    model: IValidateBusinessModel
  ): Observable<IBaseResponse> {
    const submitModel = getValidateBusinessInfoSubmitModel(model);
    const endPoint = `${this.registerBusinessBaseUrl}/draft/create`;
    return this.httpAuthPost<IBaseResponse>(endPoint, submitModel);
  }

  public validateBusinessTransactions(
    model: IValidateBusinessTransactionsSubmitModel,
    draftUid: string
  ): Observable<IBaseResponse> {
    const endpoint = `${this.registerBusinessBaseUrl}/draft/${draftUid}/validatefinancialtransactions`;
    return this.httpAuthPost<any>(endpoint, model);
  }

  public validateProgramForTransactions(
    model: IValidateBusinessTransactionsRequest
  ): Observable<IValidateBusinessTransactionsModel> {
    const endpoint = `${this.registerBusinessBaseUrl}/draft/${model.uid}/validateprogramfortransactions`;
    return this.httpAuthPost<any>(endpoint, model);
  }

  public convertCbsaProgramId(
    model: BusinessProgramIdentifier,
    draftUid: string
  ): Observable<IBaseResponse> {
    const endpoint = `${this.registerBusinessBaseUrl}/draft/${draftUid}/convertlegacyprogram`;
    return this.httpAuthPost<IBaseResponse>(endpoint, model);
  }

  public getStatus(loaderSpinnerType: LoaderSpinnerTypes): Observable<IBusinessRegistrationDraftResponse> {
    return this.httpAuthGet<any>(`${this.registerBusinessBaseUrl}/drafts`, null, loaderSpinnerType).pipe(
      map(response => (response?.businessRegistrationDrafts?.length > 0 ? response?.businessRegistrationDrafts[0] : undefined))
    );
  }

  public getPrePopulateData(
    programId: string
  ): Observable<IPrePopulateBusinessRegistrationRequest> {
    return this.httpAuthGet<IPrePopulateBusinessRegistrationRequest>(
      `${this.registerBusinessBaseUrl}/prepopulate/programId/${programId}`
    );
  }

  public canDeleteDraft(registrationDraftUid: string): Observable<boolean> {
    return this.httpAuthGet<IBaseResponse>(
      `${this.registerBusinessBaseUrl}/draft/${registrationDraftUid}/can-delete`
    ).pipe(
      map(response => (response.responseStatus === 'SUCCESS')),
    );
  }
}

